<script setup lang="ts">
import { ref, watch } from 'vue'
import { useI18n } from '@/composables/useI18n'
import { useToasts } from '@/composables/useToasts'
import { usePublishing } from '@/composables/usePublishing'

const props = defineProps<{
  visible: boolean
  entryId: string
}>()

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'published'): void
}>()

const { t } = useI18n()
const { showToast } = useToasts()
const { startPublishing } = usePublishing()

const subscriberCount = ref<number | null>(null)
const loading = ref(false)

watch(
  () => props.visible,
  async (visible) => {
    if (!visible) {
      subscriberCount.value = null
      return
    }
    loading.value = true
    try {
      const res = await fetch(`/api/admin/events/${props.entryId}/subscribers/count`)
      if (res.ok) {
        const data = await res.json()
        subscriberCount.value = data.count
      }
    } catch {
      subscriberCount.value = 0
    } finally {
      loading.value = false
    }
  }
)

async function publish() {
  // Close modal immediately
  emit('close')

  // Start publishing (progress will be shown in TimelineItem)
  await startPublishing(props.entryId, () => {
    emit('published')
  })
}

async function sendPreview() {
  try {
    const res = await fetch(`/api/admin/events/${props.entryId}/send-preview`, { method: 'POST' })
    if (res.ok) {
      showToast(t('preview-send-success'), 'success')
    } else {
      showToast('Preview failed.')
    }
  } catch {
    showToast('Preview failed.')
  }
  emit('close')
}
</script>

<template>
  <div v-if="visible" class="publish-modal-overlay" @click.self="emit('close')">
    <div class="publish-modal">
      <h3>{{ t('publish-modal-title') }}</h3>

      <div v-if="loading">
        {{ t('loading') }}
      </div>

      <div v-else>
        <p>
          {{ t('publish-modal-text-prefix') }}
          <strong>{{ subscriberCount }}</strong>
          {{ t('publish-modal-text-suffix') }}
        </p>
        <div class="publish-modal-actions">
          <button type="button" class="btn btn-ghost" @click="emit('close')">{{ t('cancel') }}</button>
          <button type="button" class="btn btn-ghost" @click="sendPreview">{{ t('preview-send-btn') }}</button>
          <button type="button" class="btn btn-primary" @click="publish">{{ t('publish-confirm') }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.publish-modal-overlay {
  position: fixed; top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.5); z-index: 1000;
  display: flex; align-items: center; justify-content: center;
}
.publish-modal {
  background: var(--bg-card, #fff); color: var(--text-main, #000); padding: 20px; border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
  max-width: 400px; width: 90%;
}
.publish-modal h3 { margin-top: 0; }
.publish-modal-actions {
  margin-top: 20px; display: flex; justify-content: flex-end; gap: 10px;
}
@media (prefers-color-scheme: dark) {
  .publish-modal { background: #1f2937; color: #f3f4f6; }
}
</style>
